import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import { AuthPlugin, getInstance } from "./auth/auth";
import APIService from "./services/APIService";
import { BootstrapVue } from "bootstrap-vue";

import "@tabler/core/dist/css/tabler.css";
import "@tabler/core/dist/js/tabler.js";
import "@tabler/core/dist/css/tabler-payments.css";
import "bootstrap-vue/dist/bootstrap-vue.css";

import VueApexCharts from "vue-apexcharts";
import AsyncComputed from "vue-async-computed";
import VueCardFormat from "vue-credit-card-validation";
import VueGtag from "vue-gtag";

import Vuelidate from "vuelidate";

import * as Sentry from "@sentry/vue";
import { Integrations } from "@sentry/tracing";

import VueTour from "vue-tour";
import VueCalendly from "vue-calendly";

require("vue-tour/dist/vue-tour.css");

Vue.use(VueTour);
Vue.use(
  VueGtag,
  {
    config: { id: "G-Z6W7CE980G" },
    enabled: process.env.VUE_APP_ANALYTICS === "true",
  },
  router
);
Vue.use(AuthPlugin, { router: router });
Vue.use(VueApexCharts);
Vue.use(AsyncComputed);
Vue.use(BootstrapVue);
Vue.use(VueCardFormat);
Vue.use(Vuelidate);
Vue.use(VueCalendly);

Vue.component("apexchart", VueApexCharts);

if (process.env.NODE_ENV == "development") {
  Vue.config.productionTip = false;
} else {
  Vue.config.productionTip = true;
}

if (process.env.VUE_APP_USE_SENTRY === "true") {
  Sentry.init({
    Vue,
    dsn: "https://80dc979ac584408e97233f9ceee3366f@o522215.ingest.sentry.io/5999352",
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: ["mac.mediabrains.com", /^\//],
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.0,
  });
}

// Tag requests so a late 401 from an expired session cannot invalidate a newer
// login that completed while the old request was still in flight.
APIService.http.interceptors.request.use((config) => {
  const auth = getInstance();
  config._authSessionVersion = auth ? auth.sessionVersion : null;
  return config;
});

// Redirect protected 401s to Login. Auth endpoints keep their existing local
// handling, and stale responses from an older auth session are ignored.
APIService.http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response && error.response.status;
    const url = (error.config && error.config.url) || "";
    const isAuthEndpoint = url.includes("api/authentication/");

    if (status === 401 && !isAuthEndpoint) {
      const auth = getInstance();
      const requestSessionVersion =
        error.config && error.config._authSessionVersion;
      const isStaleSession =
        auth &&
        typeof requestSessionVersion === "number" &&
        requestSessionVersion !== auth.sessionVersion;

      if (isStaleSession) {
        return new Promise(() => {});
      }

      if (auth) {
        auth.invalidateSession();
      }

      const current = router.currentRoute;
      if (current && current.name !== "Login") {
        return router
          .push({
            name: "Login",
            query: { returnTo: current.fullPath },
          })
          .then(
            () => new Promise(() => {}),
            () => Promise.reject(error)
          );
      }

      if (auth) {
        auth.clearUser();
      }
    }

    return Promise.reject(error);
  }
);

new Vue({
  router,
  render: (h) => h(App),
}).$mount("#app");
